// team-bus.js — Team → Workspaces panel.
//
// Phase 6 of the v4 multi-team daemon arc. Renders the BU list +
// inline create form on the Team page. The Create / Rename /
// Delete buttons are gated by `canManage` from team-page.js,
// which today resolves to `isAdmin || isOwner` from the user's
// teamState.info (server-side scope check on admin:settings is
// still the authoritative authz; this is just a UI affordance
// gate).
//
// Endpoints used:
//   GET    /v1/teams/bus            — list BUs
//   POST   /v1/teams/bus            — create custom BU
//   PATCH  /v1/teams/bus/:buId      — rename
//   DELETE /v1/teams/bus/:buId      — delete (refuses default)
//
// Single-BU tenants see only the default "All" BU; the default can
// be renamed but not deleted. Once a second BU exists, the
// per-row Delete buttons surface.
//
// Per-BU member management is deferred to a follow-up PR — this
// surface just gets the BU CRUD wired so admins can partition
// their tenant. The member roster lives behind a row-click that
// opens a member-management modal in a future iteration.

import { $ } from "./dom.js";
import { escapeHTML, ago } from "./util.js";
import { apiWithMessage } from "./api.js";
import { showConfirm, showPrompt, showToast } from "./ui.js";
import { showUpgradeRequiredFromError } from "./upgrade-prompt.js";
import { saveCheckoutResume } from "./checkout-resume.js";
import { teamState } from "./page-state.js";
// invalidateBuListCache + renderBuSwitcher keep the sidebar BU
// switcher in sync after CRUD mutations. Without these, a newly
// created BU wouldn't appear in the switcher until the 60s TTL
// expired, and a deleted BU might leave a stale selection.
import { invalidateBuListCache, renderBuSwitcher } from "./bu-switcher.js";
import { invalidateReposCache } from "./team-repos.js";
// Per-Workspace member CRUD lives in its own module; this file
// just renders the expanded section markup + wires the toggle.
import {
  isBuExpanded,
  toggleBuExpanded,
  dropBuFromExpanded,
  renderBuMembersSection,
} from "./team-bu-members.js";

// In-memory cache so re-renders during the 5s poll tick don't
// flicker the panel. Gated on (tenant_id, canManage) so:
//   - Multi-team users switching tenants invalidate naturally
//     (cacheTenant !== state.info.tenant_id). Today's dashboard
//     doesn't have in-place team-switching, but the gate prevents
//     a foot-cannon the moment it ships (per bootstrap.js's
//     "Multi-team in-place switching is coming soon" comment).
//   - A user demoted mid-session (cacheCanManage !== current)
//     stops seeing stale admin buttons that would 403 on click.
let busCache = null;
let cacheTenant = null;
let cacheCanManage = null;
let lastFetch = 0;
const FETCH_TTL_MS = 5000;

// Fetch and render. Called from renderTeamPageInner.
// `canManage` reflects the caller's UI-side admin gate
// (isAdmin || isOwner today); server still enforces the actual
// scope on every mutation.
export async function renderBusPanel(canManage) {
  const panel = $("team-bus-panel");
  const listEl = $("team-bus-list");
  const createBtn = $("team-bu-create-btn");
  if (!panel || !listEl) return;

  // Show create button only to admins. Non-admin views the list
  // but can't compose new BUs.
  if (createBtn) {
    createBtn.style.display = canManage ? "" : "none";
    // PR H (2026-05-15): badge the button for Free-tier admins so
    // they know upfront this is a Team feature, not after the
    // surprise 402. The 402-upgrade-modal flow still runs on
    // click — this is a visual hint, not a behavior change.
    //
    // PR H sweep finding #6 (LOW): gate the badge on teamState.info
    // being present so a Team-tier admin doesn't briefly see the
    // (Team) badge during the ~5s window before /v1/teams/info
    // resolves at boot. Until tier is known, render the neutral
    // "+ New workspace" label — if the user is actually Free,
    // they'll see the badge land on the next renderBusPanel call
    // (typically right after the fetch).
    const tierKnown = !!(teamState.info && typeof teamState.info.tier === "string");
    const isFree = tierKnown && teamState.info.tier === "solo";
    if (isFree) {
      createBtn.innerHTML = `+ New workspace <span style="margin-left: 4px; font-size: 9px; padding: 1px 5px; border-radius: 999px; background: rgba(255,255,255,0.2); font-weight: 600; letter-spacing: 0.05em; text-transform: uppercase;">Team</span>`;
      createBtn.title = "Custom workspaces are a Team feature. Click to upgrade.";
    } else {
      createBtn.textContent = "+ New workspace";
      createBtn.title = "";
    }
  }

  // Resolve the current tenant id for cache gating.
  // teamState.info is the /v1/teams/info response (populated by
  // fetchers.js#fetchTeamInfo); its `team_id` field is the
  // tenant_id from the coordinator's point of view.
  const tenantId = (teamState.info && teamState.info.team_id) || "";

  // Fetch BUs, with the (tenant, canManage)-gated 5s cache.
  // 403 (non-member), 404 (migration hasn't run) and any other
  // error keep the panel hidden — but log the error and toast for
  // anything other than the expected member-not-permitted /
  // migration-pending shapes, so operators debugging a 500 from
  // /v1/teams/bus don't see "panel doesn't show up" with zero
  // signal.
  const cacheValid =
    busCache &&
    cacheTenant === tenantId &&
    cacheCanManage === canManage &&
    Date.now() - lastFetch < FETCH_TTL_MS;
  if (!cacheValid) {
    try {
      const resp = await apiWithMessage("/v1/teams/bus");
      busCache = Array.isArray(resp?.bus) ? resp.bus : [];
      cacheTenant = tenantId;
      cacheCanManage = canManage;
      lastFetch = Date.now();
    } catch (err) {
      const msg = (err && err.message) || "";
      // Quietly hide for the two expected unhappy paths; toast +
      // log anything unexpected. The "forbidden" shape comes from
      // requireScope() in coordinator.ts; "not found" from a
      // tenant that pre-dates migration 034.
      if (!/forbidden|not found|unauthorized/i.test(msg)) {
        console.warn("[team-bus] /v1/teams/bus failed:", err);
        showToast("error", "Couldn't load Workspaces: " + msg);
      }
      panel.style.display = "none";
      return;
    }
  }

  if (!busCache || busCache.length === 0) {
    panel.style.display = "none";
    return;
  }
  panel.style.display = "";

  // Sort: default first, then alpha. The API already returns this
  // order, but reaffirm so manual reorderings don't drift.
  const sorted = [...busCache].sort((a, b) => {
    if (a.is_default && !b.is_default) return -1;
    if (!a.is_default && b.is_default) return 1;
    return a.name.localeCompare(b.name);
  });

  listEl.innerHTML = sorted.map((bu) => renderBuRow(bu, canManage)).join("");

  // Wire up the per-row buttons. Scope queries to listEl so a
  // future panel with overlapping class names can't accidentally
  // receive our click handlers.
  wireBuRowHandlers(canManage, listEl);
}

// invalidateBusCacheFromSwitch is a hook for any future in-place
// team-switcher implementation. The existing cache-gating on
// tenantId already makes this strictly defensive — calling it
// during a switch shaves the 5s window down to zero.
export function invalidateBusCacheFromSwitch() {
  busCache = null;
  cacheTenant = null;
  cacheCanManage = null;
  lastFetch = 0;
}

function renderBuRow(bu, isAdmin) {
  const isDefault = !!bu.is_default;
  const renamable = isAdmin && !isDefault;
  // isDefault BUs can be renamed but not deleted. Spec from the
  // backend: deleteBu refuses if is_default=1; renaming the default
  // is fine.
  const renameBtn = isAdmin
    ? `<button class="btn btn-sm bu-row-rename" data-bu-id="${escapeHTML(bu.bu_id)}" data-bu-name="${escapeHTML(bu.name)}" style="background: none; border: 1px solid var(--border); color: var(--text-secondary); font-size: 11px; padding: 4px 10px;">Rename</button>`
    : "";
  const deleteBtn = renamable
    ? `<button class="btn btn-sm bu-row-delete" data-bu-id="${escapeHTML(bu.bu_id)}" data-bu-name="${escapeHTML(bu.name)}" style="background: none; border: 1px solid var(--danger); color: var(--danger); font-size: 11px; padding: 4px 10px;">Delete</button>`
    : "";
  const defaultBadge = isDefault
    ? `<span style="font-size: 10px; padding: 2px 8px; border-radius: 999px; background: var(--accent-bg); color: var(--accent); font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; margin-left: 6px;">Default</span>`
    : "";
  // Members toggle (PR #132) — clicking expands the row to show the
  // per-Workspace member list below. The caret is rotated 90deg
  // when expanded via the .expanded class so the icon reads as
  // open/closed without an extra DOM node.
  const expanded = isBuExpanded(bu.bu_id);
  const caret = expanded ? "&#x25BC;" : "&#x25B6;";
  const membersBtn = `<button class="btn btn-sm bu-row-members" data-bu-id="${escapeHTML(bu.bu_id)}" style="background: none; border: 1px solid var(--border); color: var(--text-secondary); font-size: 11px; padding: 4px 10px;">${caret} Members</button>`;

  const expandedSection = expanded
    ? renderBuMembersSection(bu.bu_id, isAdmin)
    : "";

  return `
    <div class="bu-row-wrap" data-bu-id="${escapeHTML(bu.bu_id)}">
      <div class="bu-row" data-bu-id="${escapeHTML(bu.bu_id)}" style="display: flex; align-items: center; justify-content: space-between; padding: var(--s3) var(--s5); border-bottom: 1px solid var(--border-subtle, var(--border));">
        <div style="display: flex; align-items: center; gap: var(--s2); flex: 1; min-width: 0;">
          <span style="font-weight: 500; color: var(--text); font-size: 13px;">${escapeHTML(bu.name)}</span>
          ${defaultBadge}
          <span style="font-size: 11px; color: var(--text-tertiary); font-family: var(--font-mono);">${escapeHTML(bu.bu_id)}</span>
        </div>
        <div style="display: flex; gap: var(--s2); align-items: center;">
          <span style="font-size: 11px; color: var(--text-tertiary);">created ${escapeHTML(ago(bu.created_at))}</span>
          ${membersBtn}
          ${renameBtn}
          ${deleteBtn}
        </div>
      </div>
      ${expandedSection}
    </div>`;
}

function wireBuRowHandlers(canManage, listEl) {
  // Scope queries to listEl so a future panel with overlapping
  // class names (e.g. a "BU row" in a member-picker modal) can't
  // hijack our handlers.
  // Rename uses showPrompt (the dashboard's styled modal) rather
  // than native window.prompt — iOS Safari can dismiss native
  // dialogs unexpectedly and the styling clashes with the rest of
  // the dashboard.

  // Members toggle (PR #132). Re-renders the whole panel so the
  // expanded section markup gets inserted/removed. Could be more
  // surgical (insert/remove just the section) but a full re-render
  // is consistent with how rename/delete already work and matches
  // the rest of the panel's state-driven shape.
  listEl.querySelectorAll(".bu-row-members").forEach((btn) => {
    btn.addEventListener("click", (ev) => {
      const t = ev.currentTarget;
      const buId = t.getAttribute("data-bu-id") || "";
      toggleBuExpanded(buId);
      renderBusPanel(canManage);
    });
  });

  listEl.querySelectorAll(".bu-row-rename").forEach((btn) => {
    btn.addEventListener("click", async (ev) => {
      const t = ev.currentTarget;
      const buId = t.getAttribute("data-bu-id") || "";
      const currentName = t.getAttribute("data-bu-name") || "";
      const next = await showPrompt(`Rename workspace "${currentName}"`, "New name", {
        initialValue: currentName,
        placeholder: "Engineering",
        actionLabel: "Rename",
      });
      if (next === null || next === undefined) return;
      const trimmed = next.trim();
      if (!trimmed || trimmed === currentName) return;
      try {
        await apiWithMessage(`/v1/teams/bus/${encodeURIComponent(buId)}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: trimmed }),
        });
        showToast("success", `Renamed to "${trimmed}"`);
        busCache = null; // invalidate panel cache
        invalidateBuListCache(); // also kick the sidebar switcher
        invalidateReposCache(); // Repos panel's BU labels also need refresh
        await renderBusPanel(canManage);
        renderBuSwitcher();
      } catch (err) {
        const msg = (err && err.message) || "Failed to rename workspace";
        showToast("error", msg);
      }
    });
  });

  // Delete
  listEl.querySelectorAll(".bu-row-delete").forEach((btn) => {
    btn.addEventListener("click", async (ev) => {
      const t = ev.currentTarget;
      const buId = t.getAttribute("data-bu-id") || "";
      const buName = t.getAttribute("data-bu-name") || "";
      const ok = await showConfirm(
        `Delete workspace "${buName}"?`,
        `Repos assigned to this workspace will be unassigned (set to "no workspace"). Members of this workspace will lose their workspace-scoped role grants (their tenant-level role remains).<br><br>Historical event / lease / worktree data tagged with this workspace is preserved but will no longer surface in workspace-scoped filters.`,
        "Delete",
        { destructive: true },
      );
      if (!ok) return;
      try {
        await apiWithMessage(`/v1/teams/bus/${encodeURIComponent(buId)}`, {
          method: "DELETE",
        });
        showToast("success", `Deleted "${buName}"`);
        busCache = null;
        // Drop the deleted BU from the expanded set so the next
        // render doesn't try to fetch its members against a
        // now-deleted bu_id.
        dropBuFromExpanded(buId);
        invalidateBuListCache();
        invalidateReposCache(); // a deleted Workspace must vanish from Repos dropdowns
        await renderBusPanel(canManage);
        renderBuSwitcher();
      } catch (err) {
        const msg = (err && err.message) || "Failed to delete workspace";
        showToast("error", msg);
      }
    });
  });
}

// Wire up the inline create form. Called once on page-mount; the
// handlers stay live across re-renders because the form elements
// don't get replaced.
export function wireBuCreate() {
  const createBtn = $("team-bu-create-btn");
  const form = $("team-bu-create-form");
  const nameInput = $("team-bu-create-name");
  const submitBtn = $("team-bu-create-submit");
  const cancelBtn = $("team-bu-create-cancel");
  const errEl = $("team-bu-create-error");
  if (!createBtn || !form || !nameInput || !submitBtn || !cancelBtn) return;

  createBtn.addEventListener("click", () => {
    form.style.display = "";
    nameInput.value = "";
    if (errEl) errEl.style.display = "none";
    nameInput.focus();
  });
  cancelBtn.addEventListener("click", () => {
    form.style.display = "none";
    if (errEl) errEl.style.display = "none";
  });
  submitBtn.addEventListener("click", async () => {
    const name = (nameInput.value || "").trim();
    if (!name) {
      if (errEl) {
        errEl.textContent = "Name is required";
        errEl.style.display = "";
      }
      return;
    }
    submitBtn.disabled = true;
    try {
      await apiWithMessage("/v1/teams/bus", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      });
      showToast("success", `Created "${name}"`);
      invalidateBuListCache();
      invalidateReposCache(); // new Workspace must appear in Repos dropdowns
      renderBuSwitcher();
      busCache = null;
      form.style.display = "none";
      // Re-render passing isAdmin=true (only admins can reach this
      // path; the create button is hidden otherwise).
      await renderBusPanel(true);
    } catch (err) {
      // PR D: 402 upgrade_required from coordinator → upgrade modal.
      if (err && err.upgradeRequired) {
        // Hide the create form before showing the modal — keeping it
        // visible behind a modal makes the page feel unresolved.
        form.style.display = "none";
        // PR G (2026-05-15): save the pending workspace name so it
        // auto-creates after the Stripe round-trip. Without this,
        // the user types "Engineering" → upgrade → returns →
        // workspace is gone, they re-type.
        saveCheckoutResume("workspace", { name });
        await showUpgradeRequiredFromError(err);
        return;
      }
      const msg = (err && err.message) || "Failed to create workspace";
      if (errEl) {
        errEl.textContent = msg;
        errEl.style.display = "";
      }
    } finally {
      submitBtn.disabled = false;
    }
  });

  // Submit on Enter inside the name field.
  nameInput.addEventListener("keydown", (ev) => {
    if (ev.key === "Enter") {
      ev.preventDefault();
      submitBtn.click();
    }
  });
}

// Invalidate the cache from outside (e.g., after some other action
// might have changed BUs). Used by handlers that don't want to wait
// the full TTL.
export function invalidateBusCache() {
  busCache = null;
}
